import type { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import type {
  FormComponent,
  FormMixinComputed,
  FormMixinData,
  FormMixinMethods,
  FormMixinProps,
} from '@/mixins/form';

import form from '@/mixins/form';

const baseOptions: ThisTypedComponentOptionsWithRecordProps<
  ObjectFormComponent,
  ObjectFormMixinData,
  ObjectFormMixinMethods,
  ObjectFormMixinComputed,
  ObjectFormMixinProps
> = {
  extends: form as any,
  model: {
    prop: 'inputModel',
    event: 'save',
  },
  props: {
    inputModel: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dirty: false,
      model: null,
    };
  },
  computed: {
    isDirty: {
      get() {
        return !!this.dirty;
      },
      set(value) {
        this.dirty = !!value;
      },
    },
    modelSource() {
      return this.inputModel;
    },
  },
  methods: {
    convertModel(value) {
      return value == null ? {} : Object(value);
    },
    save(model) {
      if (model) {
        model = this.convertModel(model);
        this.model = model;
      } else {
        model = this.model;
      }
      this.isDirty = false;
      this.$emit('save', model);
    },
  },
  watch: {
    model: {
      handler(newModel, oldModel) {
        this.isDirty = !!(newModel && newModel === oldModel);
      },
      deep: true,
    },
    modelSource(newModelSource, oldModelSource) {
      if (newModelSource && newModelSource !== oldModelSource) {
        this.model = this.convertModel(newModelSource) || {};
      }
    },
  },
  created() {
    this.model = this.convertModel(this.modelSource);
  },
};

function objectForm<T extends object>(
  modelFactory?: ModelFactoryOption<T> | null
): ObjectFormMixinOptions<T> {
  if (!modelFactory) {
    return baseOptions as ObjectFormMixinOptions<T>;
  }
  if ('create' in modelFactory && typeof modelFactory.create === 'function') {
    modelFactory = modelFactory.create.bind(modelFactory);
  } else if ('from' in modelFactory && typeof modelFactory.from === 'function') {
    modelFactory = modelFactory.from.bind(modelFactory);
  } else if (typeof modelFactory !== 'function') {
    return baseOptions as ObjectFormMixinOptions<T>;
  }
  return {
    ...baseOptions,
    methods: {
      ...baseOptions.methods,
      convertModel: modelFactory,
    },
  } as ObjectFormMixinOptions<T>;
}

Object.defineProperty(objectForm, 'options', { value: baseOptions });

export default objectForm as {
  <T extends object>(modelFactory?: ModelFactoryOption<T> | null): ObjectFormMixinOptions<T>;
  readonly options: ObjectFormMixinOptions<any>;
};

export interface ModelFactory<T extends object> {
  (this: any, value?: Partial<T> | null): T;
}

export type ModelFactoryOption<T extends object> =
  | ModelFactory<T>
  | { create: ModelFactory<T> }
  | { from: ModelFactory<T> };

export type ObjectFormMixinOptions<T extends object = any> =
  ThisTypedComponentOptionsWithRecordProps<
    Vue,
    ObjectFormMixinData<T>,
    ObjectFormMixinMethods<T>,
    {},
    ObjectFormMixinProps<T>
  >;

export interface ObjectFormComponent<T extends object = any> extends FormComponent {
  /**
   * __(Prop)__
   *
   * The object received via the `v-model` directive.
   */
  readonly inputModel: Partial<T> | null | undefined;

  /**
   * __(Computed)__
   *
   * `true` if the form has unsaved changes; otherwise, `false`.
   */
  isDirty: boolean;
  /**
   * __(Computed)__
   *
   * The source object from which the `model` is created.
   */
  readonly modelSource: Partial<T> | null | undefined;

  /**
   * __(Data)__
   *
   * The value manipulated by the `isDirty` getter and setter.
   */
  dirty: boolean;
  /**
   * __(Data)__
   *
   * The object being edited.
   */
  model: T;

  /**
   * __(Method)__
   *
   * Converts the provided value to the type expected by the `model` data property.
   *
   * @param value
   * The value to convert.
   */
  convertModel(value?: Partial<T> | null): T;
  /**
   * __(Method)__
   *
   * Replaces the `model` with the provided object, sets `isDirty` to `false`, and emits
   * the `save` event.
   *
   * @param model
   * The saved model.
   */
  save(model: T): void;
}

export type ObjectFormMixinComputed<T extends object = any> = Pick<
  ObjectFormComponent<T>,
  'isDirty' | 'modelSource'
> &
  Partial<Omit<FormMixinComputed, 'isDirty'>>;

export type ObjectFormMixinData<T extends object = any> = Pick<
  ObjectFormComponent<T>,
  'dirty' | 'model'
> &
  Partial<FormMixinData>;

export type ObjectFormMixinMethods<T extends object = any> = Pick<
  ObjectFormComponent<T>,
  'convertModel' | 'save'
> &
  Partial<FormMixinMethods>;

export type ObjectFormMixinProps<T extends object = any> = Pick<
  ObjectFormComponent<T>,
  'inputModel'
> &
  Partial<FormMixinProps>;
